/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ActiveCell, TableColumn } from '../Table';

import { keyExtractor } from '#utils/PRIVATE_collectionUtils';
import * as DomUtils from '#PRIVATE_TableCommon/utils/DomUtils';

export const TABLE_PENDING_SELECTOR = 'data-oj-table-pending';
export const TABLE_BODY_SELECTOR = 'data-oj-table-body';
export const TABLE_NODATA_SELECTOR = 'data-oj-table-nodata';
export const TABLE_SPACER_SELECTOR = 'data-oj-table-spacer';
export const TABLE_LOADING_SELECTOR = 'data-oj-table-loading';
export const TABLE_LOADMORE_SELECTOR = 'data-oj-table-loadmore';
export const TABLE_FOCUSABLE_SELECTOR = 'data-oj-table-focusable';
export const TABLE_DATA_SELECTOR = 'data-oj-table-data';
export const TABLE_ROW_SELECTOR = 'data-oj-table-row';
export const TABLE_HEADER_SELECTOR = 'data-oj-table-header';
export const TABLE_HEADER_ROW_SELECTOR = 'data-oj-table-header-row';
export const TABLE_FOOTER_SELECTOR = 'data-oj-table-footer';
export const TABLE_FOOTER_ROW_SELECTOR = 'data-oj-table-footer-row';

/**
 * Returns whether or not auto focus is enabled on the specified cell
 */
export const isAutoFocusCell = <K, C>(
  rootElement: HTMLElement,
  tableId: string,
  activeCell: ActiveCell<K, C>
) => {
  const cellElement = getElementFromCell(rootElement, tableId, activeCell);
  return cellElement?.dataset['ojTableAutoFocus'] === 'enabled';
};

/**
 * Returns the key for the row at the given index
 */
export const getRowKeyForRowIndex = <K>(
  rootElement: HTMLElement,
  tableId: string,
  index: number
) => {
  const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
  return rowElements.length > index
    ? DomUtils.getRowKey<K>(rowElements[index] as HTMLElement)
    : undefined;
};

/**
 * Returns the index of the given row element
 */
export const getRowIndexForRowElement = (
  rootElement: HTMLElement,
  tableId: string,
  rowElement: HTMLElement
) => {
  return DomUtils.getRowIndexForRowElement(rootElement, tableId, rowElement, TABLE_ROW_SELECTOR);
};

/**
 * Returns the index of the row for the given key.
 */
export function getRowIndexForRowKey<K>(rootElement: HTMLElement, tableId: string, rowKey: K) {
  const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
  for (let i = 0; i < rowElements.length; i++) {
    if (rowKey === DomUtils.getRowKey(rowElements[i] as HTMLElement)) {
      return i;
    }
  }
  return undefined;
}

/**
 * Returns the column key for the given cell element
 */
export const getColumnKey = <C>(element: HTMLElement) => {
  return element.dataset['ojColumnKey'] as C;
};

/**
 * Returns the key for the column at the given index
 */
export const getColumnKeyForColumnIndex = (
  rootElement: HTMLElement,
  tableId: string,
  index: number
) => {
  const headerElements = rootElement.querySelectorAll(`[${TABLE_HEADER_SELECTOR}='${tableId}']`);
  return headerElements.length > index
    ? getColumnKey(headerElements[index] as HTMLElement)
    : undefined;
};

/**
 * Returns the index of the given column header element or footer element
 */
export const getColumnIndexForHeaderFooterElement = (
  rootElement: HTMLElement,
  tableId: string,
  cellElement: HTMLElement,
  isHeader: boolean
) => {
  const columnElements = isHeader
    ? rootElement.querySelectorAll(`[${TABLE_HEADER_SELECTOR}='${tableId}']`)
    : rootElement.querySelectorAll(`[${TABLE_FOOTER_SELECTOR}='${tableId}']`);
  const index = Array.prototype.indexOf.call(columnElements, cellElement);
  return index > -1 ? index : undefined;
};

/**
 * Helper method to get the cell key definition from an element
 */
export function logicalCellExtractor<K extends string | number, D, C extends string>(
  element: HTMLElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  cellSelector?: string,
  onlyAncestors?: boolean
): ActiveCell<K, C> | undefined {
  const selectorString =
    cellSelector != null ? cellSelector : `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`;
  const startingElement = onlyAncestors ? element.parentElement : element;
  if (startingElement != null) {
    const cellElement = startingElement.closest(selectorString) as HTMLElement;
    if (cellElement != null) {
      const cellType = DomUtils.getCellType(cellElement);
      if (cellType === 'data') {
        return {
          rowKey: keyExtractor<K>(cellElement, `[${TABLE_ROW_SELECTOR}='${tableId}']`)!,
          columnKey: getColumnKey<C>(cellElement),
          type: cellType
        };
      } else if (cellType === 'header' || cellType === 'footer') {
        return { columnKey: getColumnKey<C>(cellElement), type: cellType };
      } else if (cellType === 'noData' || cellType === 'pending') {
        return { type: cellType };
      } else if (cellType === 'loading' || cellType === 'loadMore') {
        const columnKey = getBoundaryColumnKey<K, D, C>(columnsArray, true);
        if (columnKey != null) {
          return { columnKey: columnKey, type: cellType };
        }
      }
    }
  }
  return undefined;
}

/**
 * Helper method to get an element from a cell descriptor
 */
export function getElementFromCell<K, C>(
  rootElement: HTMLElement,
  tableId: string,
  cell: ActiveCell<K, C>
): HTMLElement | undefined {
  if (cell.type === 'header') {
    // header case
    const headerElements = rootElement.querySelectorAll(`[${TABLE_HEADER_SELECTOR}='${tableId}']`);
    for (const headerElement of headerElements) {
      if (cell.columnKey === getColumnKey(headerElement as HTMLElement)) {
        return headerElement as HTMLElement;
      }
    }
  } else if (cell.type === 'footer') {
    // footer case
    const footerElements = rootElement.querySelectorAll(`[${TABLE_FOOTER_SELECTOR}='${tableId}']`);
    for (const footerElement of footerElements) {
      if (cell.columnKey === getColumnKey(footerElement as HTMLElement)) {
        return footerElement as HTMLElement;
      }
    }
  } else if (cell.type === 'data') {
    // data body case
    const rowElements = rootElement.querySelectorAll(`[${TABLE_ROW_SELECTOR}='${tableId}']`);
    for (const rowElement of rowElements) {
      if (cell.rowKey === DomUtils.getRowKey(rowElement as HTMLElement)) {
        const cellElements = rowElement.querySelectorAll(
          `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
        );
        for (const cellElement of cellElements) {
          if (cell.columnKey === getColumnKey(cellElement as HTMLElement)) {
            return cellElement as HTMLElement;
          }
        }
      }
    }
  } else if (cell.type === 'noData') {
    // no data case
    return getNoDataCellElement(rootElement, tableId);
  } else if (cell.type === 'pending') {
    // pending skeletons case
    return getPendingLayoutCellElement(rootElement, tableId);
  } else if (cell.type === 'loading') {
    // loading skeletons case
    return getLoadingCellElement(rootElement, tableId);
  } else if (cell.type === 'loadMore') {
    // loadMore skeletons case
    return getLoadMoreCellElement(rootElement, tableId);
  }
  return undefined;
}

/**
 * Returns the key of the first focusable row in the Table
 */
export const getFirstRenderedRowKey = <K>(rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getFirstRenderedRowKey<K>(rootElement, tableId, TABLE_ROW_SELECTOR);
};

/**
 * Returns the key of the last focusable row in the Table
 */
export const getLastRenderedRowKey = <K>(rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getLastRenderedRowKey<K>(rootElement, tableId, TABLE_ROW_SELECTOR);
};

/**
 * Returns the cell definition of the first rendered cell in the Table
 */
export const getFirstFocusableCell = <K extends string | number, C extends string>(
  rootElement: HTMLElement,
  tableId: string
): ActiveCell<K, C> | undefined => {
  const firstCellElement = rootElement.querySelector(
    `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
  ) as HTMLElement;
  if (firstCellElement != null) {
    const firstCellType = DomUtils.getCellType(firstCellElement);
    if (firstCellType === 'header' || firstCellType === 'footer') {
      return { columnKey: getColumnKey<C>(firstCellElement), type: firstCellType };
    } else if (firstCellType === 'noData') {
      return { type: 'noData' };
    }
    return {
      rowKey: keyExtractor<K>(firstCellElement, `[${TABLE_ROW_SELECTOR}='${tableId}']`)!,
      columnKey: getColumnKey<C>(firstCellElement),
      type: 'data'
    };
  }
  return undefined;
};

/**
 * Helper function to get the first or last column key.
 */
export const getBoundaryColumnKey = <K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  isFirst: boolean
) => {
  if (columnsArray.length > 0) {
    return isFirst ? columnsArray[0].key : columnsArray[columnsArray.length - 1].key;
  }
  return undefined;
};

/**
 * Helper method to get the 'pending layout' cell
 */
export const getPendingLayoutCellElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_PENDING_SELECTOR);
};

/**
 * Helper method to get the 'loading' cell
 */
export const getLoadingCellElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_LOADING_SELECTOR);
};

/**
 * Helper method to get the 'loadMore' cell
 */
export const getLoadMoreCellElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_LOADMORE_SELECTOR);
};

/**
 * Helper method to get the table body
 */
export const getTableBodyElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_BODY_SELECTOR);
};

/**
 * Helper method to get the 'noData' cell
 */
export const getNoDataCellElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_NODATA_SELECTOR);
};

/**
 * Helper method to get the 'spacer' row
 */
export const getSpacerRowElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_SPACER_SELECTOR);
};

/**
 * Helper method to get the footer row
 */
export const getFooterRowElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_FOOTER_ROW_SELECTOR);
};

/**
 * Helper method to get the header row
 */
export const getHeaderRowElement = (rootElement: HTMLElement, tableId: string) => {
  return DomUtils.getElementBySelector(rootElement, tableId, TABLE_HEADER_ROW_SELECTOR);
};

/**
 * Helper method to determine if the 'loading' cell is present in the Table
 */
export const hasLoadingCell = (rootElement: HTMLElement, tableId: string) => {
  return getLoadingCellElement(rootElement, tableId) != null;
};

/**
 * Helper method to determine if the 'loadMore' cell is present in the Table
 */
export const hasLoadMoreCell = (rootElement: HTMLElement, tableId: string) => {
  return getLoadMoreCellElement(rootElement, tableId) != null;
};

/**
 * Helper method to determine if the 'noData' cell is present in the Table
 */
export const hasNoDataCell = (rootElement: HTMLElement, tableId: string) => {
  return getNoDataCellElement(rootElement, tableId) != null;
};

/**
 * Helper method to determine if footer cells are present in the Table
 */
export const hasFooterCells = (rootElement: HTMLElement, tableId: string) => {
  return getFooterRowElement(rootElement, tableId) != null;
};
